import "./EntryView.less";

import { notification, Button, Icon, Tooltip } from "antd";
import * as QueryString from "query-string";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";

import { EntryApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps } from "lib/data/interfaces";
import { ComponentService, LoginService, StateService, UtilityService } from "lib/services";

import { AuthorizedComponent, GradientBar } from "components/general";
import { SectionPage } from "components/pages";
import { Entry, Permission, Section } from "lib/data/model";
import { Event, EventDispatcher } from "lib/EventDispatcher";
import { cloneDeep } from "lodash";

interface IEntryViewProps extends IBaseProps, RouteComponentProps<any> {

}

interface IEntryViewState {
	isLoading: boolean;
	isLoaded: boolean;
	searchWords?: string[];
	buttonText: string;
	checkQueryString?: string;
	isEditingEntryName: boolean;
	editEntryNameText: string;
	isEntryNameSaving: boolean;
	canEditMetadata: boolean;
}

interface IEntryViewLocalState {
	isChildClicked: boolean;
}


export class EntryView extends React.Component<IEntryViewProps, IEntryViewState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private searchWords: string[] | undefined;
	private checkQueryString: string | undefined;

	private entry!: Entry;
	private hierarchies: Section[][] = [];
	private permission!: Permission;

	private localState: IEntryViewLocalState = {
		isChildClicked: false
	};

	constructor(props: IEntryViewProps) {

		super(props);

		this.state = {
			isLoading: true,
			isLoaded: false,
			buttonText: "Remove Highlighting",
			checkQueryString: "",
			isEditingEntryName: false,
			editEntryNameText: "",
			isEntryNameSaving: false,
			canEditMetadata: false
		};
	}

	public async componentWillMount() {

		LoginService.getUserIfExists()
			.then((user) => this.setState({ canEditMetadata: !!user }));

		// Register For Logged In Event
		EventDispatcher.register(Event.LoggedIn, this.onLoginStatusChange);

		// Register For Logged Out Event
		EventDispatcher.register(Event.LoggedOut, this.onLoginStatusChange);

		// Get Search Query
		const searchQueryString = QueryString.parse(this.props.location.search).q;
		let searchQuery: string | undefined;

		if (searchQueryString) {

			// Check If Query Returns Multiple
			if (Array.isArray(searchQuery)) {

				// Concatenate Search Queries
				searchQuery = searchQuery.join(" ");
			}
			else {

				// Store Search Query As-Is
				searchQuery = searchQueryString as string;
			}

			let strictKeywords: string[] = [];

			if (searchQuery !== undefined) {

				const { quotedValues, remainingString } = UtilityService.extractReduceQuotedStrings(searchQuery);
				searchQuery = remainingString;
				strictKeywords = quotedValues;
			}
			this.searchWords = (searchQuery as string).split(" ");
			this.searchWords.push(...strictKeywords);
			this.checkQueryString = (searchQuery as string);
		}

		// Initialize Promises
		const promises: Array<Promise<void>> = [];

		// Get Entry
		let entry: Entry | undefined;

		// Set Promises
		promises.push(new Promise(async (resolve) => { entry = await EntryApi.getEntry(this.props.match.params.id, true); resolve(); }));
		promises.push(new Promise(async (resolve) => { this.hierarchies = await EntryApi.getHierarchy(this.props.match.params.id); resolve(); }));

		// Await Promises
		await Promise.all(promises);

		// Check Entry
		if (entry) {

			// Store Entry
			this.entry = entry;

			// Set the required permissions for updating the entry record
			this.permission = this.permission;

			// Indicate Entry Is Loaded
			StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, searchWords: this.searchWords, checkQueryString: this.checkQueryString }), this.apiCancellationToken);
		}
		else {

			// Indicate Entry Not Found
			StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
		}

	}

	public async componentWillReceiveProps(newProps: IEntryViewProps) {

		this.searchWords = undefined;

		// Get Search Query
		const searchQueryString = QueryString.parse(newProps.location.search).q;
		let searchQuery: string | undefined;

		if (searchQueryString) {

			// Check If Query Returns Multiple
			if (Array.isArray(searchQuery)) {

				// Concatenate Search Queries
				searchQuery = searchQuery.join(" ");
			}
			else {

				// Store Search Query As-Is
				searchQuery = searchQueryString as string;
			}

			this.searchWords = (searchQuery as string).split(" ");
		}

		// Check For Different Section ID
		if (this.state.isLoaded && this.entry.id !== newProps.match.params.id) {

			// Initialize Promises
			const promises: Array<Promise<void>> = [];

			// Get Entry
			let entry: Entry | undefined;

			// Set Promises
			promises.push(new Promise(async (resolve) => { entry = await EntryApi.getEntry(newProps.match.params.id); resolve(); }));
			promises.push(new Promise(async (resolve) => { this.hierarchies = await EntryApi.getHierarchy(newProps.match.params.id); resolve(); }));

			// Await Promises
			await Promise.all(promises);

			// Check Entry
			if (entry) {

				// Store Entry
				this.entry = entry;

				// Indicate Entry Is Loaded
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: true, searchWords: this.searchWords, checkQueryString: this.checkQueryString }), this.apiCancellationToken);
			}
			else {

				// Indicate Entry Not Found
				StateService.setCancelableState(() => this.setState({ isLoading: false, isLoaded: false }), this.apiCancellationToken);
			}
		}
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();

		// Unregister For Logged In Event
		EventDispatcher.unregister(Event.LoggedIn, this.onLoginStatusChange);

		// Unregister For Logged Out Event
		EventDispatcher.unregister(Event.LoggedOut, this.onLoginStatusChange);
	}

	public render() {

		// Define Class Name
		const className = "entry-view" + (this.props.className ? ` ${this.props.className}` : "");

		// Check For Loaded Entry
		if (!this.state.isLoading) {

			// Check Entry
			if (this.state.isLoaded) {

				const gradientBarActions = [];

				if (this.state.isEditingEntryName) {

					gradientBarActions.push(...[
						(
							<AuthorizedComponent key="entry-name-save" permission={this.permission}>
								<Tooltip title="Save Entry Name">
									<Button
										icon="check"
										onClick={this.onEntryNameSave}
										loading={this.state.isEntryNameSaving}
									// size="large"
									/>
								</Tooltip>
							</AuthorizedComponent>
						),
						(
							<AuthorizedComponent key="entry-name-save-cancel" permission={this.permission}>
								<Tooltip title="Cancel Entry Name Edit">
									<Button
										icon="close"
										type="danger"
										// size="large"
										onClick={this.onCancelEntryNameEdit}
										loading={this.state.isEntryNameSaving}
									/>
								</Tooltip>
							</AuthorizedComponent>
						)
					]);
				}
				else {

					gradientBarActions.push(
						<AuthorizedComponent key="toggle-name-edit" permission={this.permission}>
							<Tooltip title="Edit Entry Name">
								<Button
									icon="form"
									// size="large"
									onClick={this.onEntryNameToggleEdit}
								/>
							</Tooltip>
						</AuthorizedComponent>
					);
				}

				return (

					<section className={className}>

						{/* Toggle Search Keyword Highlighting and Return to Results */}

						{(
							this.state.checkQueryString !== undefined
								?
								(
									<div className="toggle-highlighting">

										<Button.Group>
											<Button className="button-group" icon="left" onClick={this.onReturnButtonClick}>Return to Results</Button>
											<Button onClick={this.onHighlightToggle}>{this.state.buttonText}<Icon type="highlight" /></Button>
										</Button.Group>

									</div>
								)
								:
								null
						)}

						{/* Entry Title */}
						<GradientBar
							className="entry-view-header"
							title={this.entry.name}
							size="large"
							actions={gradientBarActions}
							highlightWords={this.state.searchWords}
							isEditing={this.state.isEditingEntryName}
							onNameChange={this.onEntryNameTextEdit}
						/>

						{/* Entry Content Information */}
						<section className="entry-view-content">

							<GradientBar
								className="entry-parents-group"
								size="small"
								title="References:"
								childrenClassName="entry-parent-section-parents"
							>

								{this.hierarchies.filter((i) => i[0].published !== undefined).map((hierarchy) => {

									// Ensure Hierarchy Exists
									if (hierarchy.length > 0) {

										// Clone Hierarchy
										const clonedHierarchy = cloneDeep(hierarchy);

										// Get Immediate Parent
										const immediateParent = clonedHierarchy.pop() as Section;

										// Get Parent's Name
										const parentName = immediateParent.published!.name;

										// Ensure Parent Name Exists
										if (parentName) {

											return (
												<GradientBar
													title={parentName}
													superTitle={ComponentService.getHierarchyString(clonedHierarchy)}
													key={immediateParent.id}
													onClick={() => this.onHierarchySectionClick(immediateParent)}
												/>
											);
										}
									}

									// Return Nothing
									return null;
								})}

							</GradientBar>

						</section>

					</section>

				);
			}
			else {

				// Render Error
				return (
					<section>
						<h3>Record Not Found</h3>
					</section>
				);
			}
		}
		else {

			// Render Loading
			return (
				<section>
					<h4>Loading...</h4>
				</section>
			);
		}
	}

	/**
	 * Called when saving the entry name
	 */
	private onEntryNameSave = async () => {

		StateService.setCancelableState(() => this.setState({
			isEntryNameSaving: true
		}), this.apiCancellationToken);

		// Clone the entry so we don't change this.entry
		const clonedEntry = cloneDeep(this.entry);

		clonedEntry.name = this.state.editEntryNameText;

		// Save the entry
		const savedEntry = await UtilityService.asyncTimeout(EntryApi.saveEntry(clonedEntry));

		if (savedEntry !== undefined) {

			// If we have successfully saved the entry, update this.entry to the saved entry
			this.entry = savedEntry;

			StateService.setCancelableState(() => this.setState({
				isEditingEntryName: false
			}), this.apiCancellationToken);

			notification.success({
				message: "Entry Name Successfully Updated"
			});
		}
		else {

			notification.error({
				message: "There was an issue while attempting to save the entry name"
			});
		}

		StateService.setCancelableState(() => this.setState({
			isEntryNameSaving: false
		}), this.apiCancellationToken);
	}

	/**
	 * Updates the edit entry name text
	 */
	private onEntryNameTextEdit = (newValue: string) => {

		StateService.setCancelableState(() => this.setState({
			editEntryNameText: newValue
		}), this.apiCancellationToken);
	}

	/**
	 * Toggles entry name edit mode to true
	 */
	private onEntryNameToggleEdit = () => {

		StateService.setCancelableState(() => this.setState({
			isEditingEntryName: true,
			editEntryNameText: this.entry.name
		}), this.apiCancellationToken);
	}

	/**
	 * Cancels an edit of the entry name
	 */
	private onCancelEntryNameEdit = () => {

		StateService.setCancelableState(() => this.setState({
			isEditingEntryName: false,
			editEntryNameText: ""
		}), this.apiCancellationToken);
	}

	private onHierarchySectionClick = (section?: Section) => {

		// Check Child Clicked Local State
		if (!this.localState.isChildClicked) {

			// Set Child Clicked Local State
			this.localState.isChildClicked = true;

			// Check For Section
			if (section !== undefined) {

				// Redirect To New Section
				this.props.history.push(`${SectionPage.BasePath}/${section.id}`);
			}
			else {

				// Redirect To Root Directory Page
				this.props.history.push("/");
			}

			// Set Child Clicked Local State
			this.localState.isChildClicked = false;
		}
	}

	private onHighlightToggle = () => {

		if (this.state.searchWords) {
			StateService.setCancelableState(() => this.setState({ searchWords: undefined, buttonText: "Show Highlighting" }), this.apiCancellationToken);
		}
		else {
			StateService.setCancelableState(() => this.setState({ searchWords: this.searchWords, buttonText: "Remove Highlighting" }), this.apiCancellationToken);
		}
	}

	private onReturnButtonClick = () => {

		this.props.history.goBack();
	}

	private onLoginStatusChange = () => {

		LoginService.getUserIfExists()
			.then((user) => this.setState({ canEditMetadata: !!user }));
	}
}
